import { useEffect, useRef } from "react";
import { Button } from "./ui/Button";

export interface DirtyStateDialogProps {
  /** Whether the dialog is currently visible. */
  open: boolean;
  /** Cancel handler — user dismisses the dialog and stays on the current step. */
  onCancel: () => void;
  /** Confirm handler — user accepts losing their selections and continues. */
  onConfirm: () => void;
  /** Optional title (default: "Discard your configuration?"). */
  title?: string;
  /** Optional descriptive message. */
  description?: string;
  /** Label for the cancel button (default: "Cancel"). */
  cancelLabel?: string;
  /** Label for the confirm button (default: "Discard & continue"). */
  confirmLabel?: string;
}

/**
 * DirtyStateDialog — confirmation modal shown when the customer attempts a
 * destructive in-widget navigation (e.g. "Change model" with selections, or
 * stepper-back from Step 2/3) and we don't want to silently throw their work
 * away.
 *
 * Implements an `alertdialog` with focus trap and Esc-to-cancel. Initial
 * focus lands on the Cancel button (safer default).
 */
export function DirtyStateDialog({
  open,
  onCancel,
  onConfirm,
  title = "Discard your configuration?",
  description = "If you change model now, your selected accessories will be cleared. This cannot be undone.",
  cancelLabel = "Cancel",
  confirmLabel = "Discard & continue",
}: DirtyStateDialogProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Auto-focus Cancel on open + Esc-to-cancel + simple focus trap.
  useEffect(() => {
    if (!open) return;

    cancelRef.current?.focus();

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        onCancel();
        return;
      }
      if (e.key === "Tab") {
        // Trap focus between Cancel and Confirm buttons.
        const cancel = cancelRef.current;
        const confirm = confirmRef.current;
        if (!cancel || !confirm) return;
        if (e.shiftKey && document.activeElement === cancel) {
          e.preventDefault();
          confirm.focus();
        } else if (!e.shiftKey && document.activeElement === confirm) {
          e.preventDefault();
          cancel.focus();
        }
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onCancel]);

  if (!open) return null;

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="apex-dirty-dialog-title"
      aria-describedby="apex-dirty-dialog-description"
      ref={containerRef}
      className="apex-fixed apex-inset-0 apex-z-50 apex-flex apex-items-center apex-justify-center apex-bg-black/50 apex-p-4"
      onClick={(e) => {
        // Backdrop click cancels — click events bubbling up from the inner
        // panel will not match the wrapping div's currentTarget.
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div className="apex-w-full apex-max-w-sm apex-rounded-lg apex-bg-background apex-shadow-lg apex-border apex-border-border apex-p-5 apex-flex apex-flex-col apex-gap-4">
        <div>
          <h2
            id="apex-dirty-dialog-title"
            className="apex-text-base apex-font-semibold apex-text-foreground apex-font-display"
          >
            {title}
          </h2>
          <p
            id="apex-dirty-dialog-description"
            className="apex-text-sm apex-text-muted-foreground apex-mt-1.5"
          >
            {description}
          </p>
        </div>
        <div className="apex-flex apex-items-center apex-justify-end apex-gap-2">
          <Button ref={cancelRef} variant="ghost" size="sm" onClick={onCancel}>
            {cancelLabel}
          </Button>
          <Button ref={confirmRef} variant="destructive" size="sm" onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
